import { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import detection from './detection'
import {
  IS_MOBILE,
  IS_TABLET,
  IS_DESKTOP,
  IS_WIDESCREEN,
  MOBILE,
  TABLET,
  DESKTOP,
  WIDESCREEN
} from './constants'

/**
 * List of mappings for the property name and the detection name.
 * @type {[[string,string]]}
 */
export const mappings = [
  [IS_MOBILE, MOBILE],
  [IS_TABLET, TABLET],
  [IS_DESKTOP, DESKTOP],
  [IS_WIDESCREEN, WIDESCREEN]
]

/**
 * @param {JSX.IntrinsicAttributes} props
 * @param {React.ReactNodeArray} props.children
 * @returns {JSX.Element}
 */
export default function IsViewport ({ children, ...props }) {
  const [shouldRender, setShouldRender] = useState(() => shouldReRender(props))
  /**
   * @type {function(MediaQueryListEvent):void}
   */
  const onMediaUpdate = useCallback(event => {
    setShouldRender(event.matches)
  }, [])
  const prevProps = useRef({})

  useEffect(() => {
    setupListeners(onMediaUpdate, prevProps.current, props)
    prevProps.current = props
  }, [props])

  useEffect(() => {
    return () => {
      setupListeners(onMediaUpdate, prevProps.current, {})
    }
  }, [])

  return shouldRender ? children : null
}

/**
 * Add and remove listeners to each MediaQueryList. If a prop was added,
 * add the listener, if removed, remove it.
 *
 * @param {function(MediaQueryListEvent):void} onMediaUpdate Listener to add/remove.
 * @param {JSX.IntrinsicAttributes} props Previous props to check
 * @param {JSX.IntrinsicAttributes} nextProps Next props to check
 */
function setupListeners (onMediaUpdate, props, nextProps) {
  const detections = detection()
  mappings.forEach(([propName, deviceType]) => {
    if (nextProps[propName] && !props[propName]) {
      // check added
      detections[deviceType].addListener(onMediaUpdate)
    }
    if (props[propName] && !nextProps[propName]) {
      // check removed
      detections[deviceType].removeListener(onMediaUpdate)
    }
  })
}

/**
 *
 * @param {JSX.IntrinsicAttributes} props Props to check if they match
 * @returns {boolean} Should the component re-render
 */
function shouldReRender (props) {
  const detections = detection()
  return mappings.some(
    ([propName, deviceType]) =>
      props[propName] && detections[deviceType].matches
  )
}

IsViewport.mappings = mappings

IsViewport.propTypes = {
  [IS_MOBILE]: PropTypes.bool,
  [IS_TABLET]: PropTypes.bool,
  [IS_DESKTOP]: PropTypes.bool,
  [IS_WIDESCREEN]: PropTypes.bool
}
